import { Dialog } from '@material-ui/core';
import React from 'react';

// specific to this view
import {
  getPublishersByPartner,
  getPublishersByPartnerDataShare,
  getPublishersByPartnerDataStore,
  IPublishersByPartner,
} from '~/src/dash/api/publisher';
import {
  IListTableColumn,
  IPublishersByPartnerRowData,
  ListTable,
} from '~/src/dash/components/list-table';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { navigate, RouteDashboards } from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { noopVoid } from '~/src/dash/utils/noop';
import { routeName } from '~/src/dash/utils/routes';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

type AllowedPanel = 'default' | 'datashare' | 'datastore';
interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  // api returns publishers ID/Name pairs associated with partners
  publishersByPartner: IPublishersByPartner;
  // api returns publishers ID/Name pairs associated with partners
  publishersByPartnerDataShare: IPublishersByPartner;
  // api returns publishers ID/Name pairs associated with partners
  publishersByPartnerDataStore: IPublishersByPartner;

  // indicates when render is allowed
  loading: boolean;
  selectedPanel: AllowedPanel;
  openPublisherSelectTable: boolean;
  selectedRowIdx: number;
  snackbarMessage: string;
  snackbarError: boolean;
}

const columns: IListTableColumn[] = [
  {
    field: 'Partner',
    title: 'Partner',
    renderType: 'normal',
    searchable: false,
    maxWidth: '200px',
  },
  {
    field: 'PublishersOptedIn',
    title: 'Publishers Opted In',
    renderType: 'modal',
    searchable: false,
    maxWidth: '100px',
  },
];

const formatPublisherList = (
  publishersByPartner: IPublishersByPartner,
): IPublishersByPartnerRowData[] => {
  return Object.keys(publishersByPartner).map(
    (partner: string, idx: number): IPublishersByPartnerRowData => {
      return {
        ID: idx,
        Partner: partner,
        PublishersOptedIn: publishersByPartner[partner].length,
        Name: '',
      };
    },
  );
};

export const AudienceDataShareListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    displayName: 'IPTraceListView';

    constructor(props: IProps) {
      super(props);
      this.state = {
        publishersByPartner: {} as IPublishersByPartner,
        publishersByPartnerDataStore: {} as IPublishersByPartner,
        publishersByPartnerDataShare: {} as IPublishersByPartner,

        loading: false,
        selectedPanel: 'default',
        openPublisherSelectTable: false,
        selectedRowIdx: 0,
        snackbarMessage: '',
        snackbarError: false,
      };
    }

    // --------------- EVENT NOTIFICATION HANDLER ---------------
    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    componentDidMount() {
      setTimeout(() => {
        this.setState({
          loading: false,
        });
      }, 2000);

      this.setState(
        {
          loading: true,
        },
        () => {
          Promise.all([
            getPublishersByPartner(),
            getPublishersByPartnerDataShare(),
            getPublishersByPartnerDataStore(),
          ])
            .then((res: any) => {
              const [resPixelDrop, resDataShare, resDataStore] = res;
              this.setState({
                publishersByPartner: resPixelDrop.publishers,
                publishersByPartnerDataShare: resDataShare.publishers,
                publishersByPartnerDataStore: resDataStore.publishers,
                loading: false,
              });
            })
            .catch(() => {
              this.setState({
                snackbarMessage: 'failed to get publishers list',
                snackbarError: true,
                loading: false,
              });
            });
        },
      );
    }

    handleClosePublisherSelectDialog = () => {
      this.setState({
        openPublisherSelectTable: false,
      });
    };

    handleOpenPublisherSelectDialog = (selectedPanel: AllowedPanel, selectedRowIdx: number) => {
      this.setState({
        openPublisherSelectTable: true,
        selectedPanel,
        selectedRowIdx,
      });
    };

    renderPublisherSelector(
      publishersByPartner: IPublishersByPartner,
      data: IPublishersByPartnerRowData[],
    ) {
      const { openPublisherSelectTable, selectedRowIdx } = this.state;

      return (
        !!data[selectedRowIdx] && (
          <Dialog
            fullWidth
            maxWidth='sm'
            keepMounted
            open={openPublisherSelectTable}
            onClose={this.handleClosePublisherSelectDialog}
          >
            <ModalSelectTable
              title='Publishers'
              items={publishersByPartner[data[selectedRowIdx].Partner]}
              onSelectRow={(publisherID: string) => {
                this.setState(
                  {
                    openPublisherSelectTable: false,
                  },
                  () => {
                    navigate(RouteDashboards, {
                      p: publisherID,
                    });
                  },
                );
              }}
              interactive={true}
            />
          </Dialog>
        )
      );
    }

    render() {
      const {
        publishersByPartner,
        publishersByPartnerDataShare,
        publishersByPartnerDataStore,
        loading,
        selectedPanel,
        snackbarError,
        snackbarMessage,
      } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const data = formatPublisherList(publishersByPartner);
      const dataShare = formatPublisherList(publishersByPartnerDataShare);
      const dataStore = formatPublisherList(publishersByPartnerDataStore);

      let selectedPublishers = publishersByPartner;
      let selectedData = data;
      if (selectedPanel === 'datashare') {
        selectedPublishers = publishersByPartnerDataShare;
        selectedData = dataShare;
      } else if (selectedPanel === 'datastore') {
        selectedPublishers = publishersByPartnerDataStore;
        selectedData = dataStore;
      }

      if (loading) {
        return <LoadingView active={true} text='Loading Publishers' />;
      }

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            <div style={{ marginRight: 'auto', marginLeft: 'auto', maxWidth: '600px' }}>
              <div style={{ marginBottom: '20px' }}>
                <ListTable
                  title='Pixel Drops Overview'
                  columns={columns}
                  data={data}
                  handleOpenModal={(_: any, idx: number) =>
                    this.handleOpenPublisherSelectDialog('default', idx)
                  }
                  handleSetUpdating={noopVoid}
                  isLoading={loading}
                  isUpdating={false}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <ListTable
                  title='Data Share Overview'
                  columns={columns}
                  data={dataShare}
                  handleOpenModal={(_: any, idx: number) =>
                    this.handleOpenPublisherSelectDialog('datashare', idx)
                  }
                  handleSetUpdating={noopVoid}
                  isLoading={loading}
                  isUpdating={false}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <ListTable
                  title='Data Store Overview'
                  columns={columns}
                  data={dataStore}
                  handleOpenModal={(_: any, idx: number) =>
                    this.handleOpenPublisherSelectDialog('datastore', idx)
                  }
                  handleSetUpdating={noopVoid}
                  isLoading={loading}
                  isUpdating={false}
                />
              </div>
              <SnackbarView
                open={!!snackbarMessage}
                error={!!snackbarError}
                onClose={this.handleCloseSnackbar}
              >
                {snackbarMessage}
              </SnackbarView>
              {this.renderPublisherSelector(selectedPublishers, selectedData)}
            </div>
          </Panel>
        </Panels>
      );
    }
  },
);
